import {
  getClipboardFile,
  getEmailClientsMailLink,
  getFileUploadAttachmentLink,
  getMessengersAttachmentLink,
  getMicRecordFile,
  getPrinterDocumentLink,
  getScreenshotLink,
  getShadowCopyingFile,
  getWebMailAttachmentLink,
  getWebcamsAndMicrophonesFile,
} from '@/api/methods/computers';

// Files of monitoring records, opened with the user's own session (the cookie routes of the web backend),
// so the rights of the log pages apply. A reference is {kind, recordId, attachmentId, filename, name}:
// the evidence item lists them in `files`, a file read by the assistant has its own in card.file.

const positive = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

// kind -> { url } opens in a new tab, { download } saves through the download helpers of the log pages.
const OPENERS = {
  screenshot: { url: (cid, ref) => getScreenshotLink(cid, ref.filename) },
  clipboard: { download: (cid, ref) => getClipboardFile(cid, ref.recordId) },
  printer: { download: (cid, ref) => getPrinterDocumentLink(cid, ref.recordId) },
  'file-upload': { url: (cid, ref) => getFileUploadAttachmentLink(cid, ref.recordId, ref.attachmentId) },
  'web-mail': { url: (cid, ref) => getWebMailAttachmentLink(cid, ref.recordId, ref.attachmentId) },
  // The web interface has no route for one attachment of a client letter: the whole letter (.eml) opens.
  'email-clients': { url: (cid, ref) => getEmailClientsMailLink(cid, ref.recordId) },
  // recordId is the N of the messenger record "file-N"; the route takes the whole id.
  messengers: { url: (cid, ref) => getMessengersAttachmentLink(cid, `file-${ref.recordId}`) },
  'shadow-copyings': { download: (cid, ref) => getShadowCopyingFile(cid, ref.recordId) },
  'mic-records': { download: (cid, ref) => getMicRecordFile(cid, ref.recordId) },
  'webcams-and-microphones': { download: (cid, ref) => getWebcamsAndMicrophonesFile(cid, ref.recordId) },
};
const NEEDS_ATTACHMENT = ['file-upload', 'web-mail'];

/** Whether the web interface can open a file reference (a kind without a browser route cannot). */
export function canOpen(ref) {
  if (!ref || !OPENERS[ref.kind]) return false;
  if (ref.kind === 'screenshot') return !!ref.filename;
  if (NEEDS_ATTACHMENT.includes(ref.kind) && !positive(ref.attachmentId)) return false;
  return !!positive(ref.recordId);
}

/** Files of an evidence item that can be opened; a client letter with attachments is one letter. */
export function fileRefs(item) {
  const letters = new Set();
  return (item && Array.isArray(item.files) ? item.files : []).filter(canOpen).reduce((result, ref) => {
    if (ref.kind !== 'email-clients') return [...result, ref];
    if (letters.has(ref.recordId)) return result;
    letters.add(ref.recordId);
    return [...result, { ...ref, attachmentId: null, name: 'Письмо' }];
  }, []);
}

/** Label of the open action: a client letter is opened as a whole, with its attachments. */
export function openLabel(ref) {
  if (ref.kind === 'email-clients') return ref.attachmentId ? 'Открыть письмо с вложением' : 'Открыть письмо';
  return 'Открыть файл';
}

/** Opens or downloads a file; resolves with an error text or ''. */
export async function openFile(computerId, ref) {
  const cid = positive(computerId);
  if (!cid || !canOpen(ref)) return 'Этот файл нельзя открыть из веб-интерфейса';
  const opener = OPENERS[ref.kind];
  if (opener.url) {
    window.open(opener.url(cid, ref), '_blank', 'noopener');
    return '';
  }
  const result = await opener.download(cid, ref);
  if (result && result.success) return '';
  return result && result.status === 404 ? 'Файл не найден на сервере' : 'Не удалось получить файл';
}
